import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import type { DialogConfig } from '@wirechunk/ui';
import { AlertDialog, Button, Dialog, DialogContext, Flex } from '@wirechunk/ui';
import type { CSSProperties, FunctionComponent, ReactNode } from 'react';
import { Fragment, useCallback, useState } from 'react';
import { preventDefault } from '../util/prevent-default.ts';

export type ConfirmDialogProps = {
  // Default: 'Confirmation'
  header?: string;
  className?: string;
  acceptLabel?: string;
  rejectLabel?: string;
  acceptClassName?: string;
  rejectClassName?: string;
  onAccept: () => void;
  onReject?: () => void;
};

const maxWidths = {
  md: '410px',
  lg: '600px',
  xl: '900px',
} as const;

const maxHeightFullStyle: CSSProperties = {
  // The values var(--space-6) and max(var(--space-6), 6vh) come from .rt-BaseDialogScrollPadding.
  maxHeight: 'calc(100dvh - var(--space-6) - max(var(--space-6), 6vh))',
};

type DialogProviderProps = {
  children: ReactNode;
};

export const DialogProvider: FunctionComponent<DialogProviderProps> = ({ children }) => {
  const [dialog, setDialog] = useState<ReactNode>(null);
  const [confirmDialog, setConfirmDialog] = useState<ReactNode>(null);

  const showDialog = useCallback((config: DialogConfig | null) => {
    if (!config) {
      setDialog(null);
      setConfirmDialog(null);
      return;
    }

    if ('confirm' in config) {
      setConfirmDialog(
        <AlertDialog.Root
          open
          onOpenChange={(open) => {
            if (!open) {
              setConfirmDialog(null);
            }
          }}
        >
          <AlertDialog.Content>
            <AlertDialog.Title>{config.header ?? 'Confirmation'}</AlertDialog.Title>
            {config.customAccessibleDescription ? (
              <Fragment>
                <VisuallyHidden>
                  <AlertDialog.Description>
                    {config.customAccessibleDescription}
                  </AlertDialog.Description>
                </VisuallyHidden>
                {config.confirm}
              </Fragment>
            ) : (
              <AlertDialog.Description>{config.confirm}</AlertDialog.Description>
            )}
            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button
                  variant="soft"
                  color="gray"
                  onClick={() => {
                    setConfirmDialog(null);
                    config.onReject?.();
                  }}
                >
                  {config.rejectLabel ?? 'Cancel'}
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button
                  color={config.acceptButtonColor}
                  onClick={() => {
                    setConfirmDialog(null);
                    config.onAccept();
                  }}
                >
                  {config.acceptLabel ?? 'Confirm'}
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>,
      );
    } else {
      const lightDismiss = config.lightDismiss ?? true;
      setDialog(
        <Dialog.Root
          open
          onOpenChange={(open) => {
            if (!open) {
              setDialog(null);
            }
          }}
        >
          <Dialog.Content
            maxWidth={maxWidths[config.width ?? 'md']}
            style={(config.maxHeightFull ?? true) ? maxHeightFullStyle : undefined}
            onPointerDownOutside={lightDismiss ? undefined : preventDefault}
            onEscapeKeyDown={lightDismiss ? undefined : preventDefault}
          >
            <Dialog.Title>{config.header}</Dialog.Title>
            {config.content}
          </Dialog.Content>
        </Dialog.Root>,
      );
    }
  }, []);

  return (
    <DialogContext value={showDialog}>
      {children}
      {dialog}
      {confirmDialog}
    </DialogContext>
  );
};
